import { ROLE_SUPER_ADMIN } from "../constants/role";

/**
 * Restricts the visible machine-group list to a user's assigned groups.
 *
 *  - Super Admin always sees every group, regardless of assignment.
 *  - A user with no groups assigned sees every group (safe default so
 *    existing users aren't locked out the moment this restriction shipped).
 *  - A user with groups assigned sees only those groups.
 */
export function filterGroupsForUser(allGroups, role, userGroupCodes) {
  if (role === ROLE_SUPER_ADMIN) return allGroups;
  if (!userGroupCodes || userGroupCodes.length === 0) return allGroups;
  return allGroups.filter((g) => userGroupCodes.includes(g.code));
}

/** Same 3 rules as filterGroupsForUser, applied to Plant Groups instead. */
export function filterPlantsForUser(allPlants, role, userPlantCodes) {
  if (role === ROLE_SUPER_ADMIN) return allPlants;
  if (!userPlantCodes || userPlantCodes.length === 0) return allPlants;
  return allPlants.filter((p) => userPlantCodes.includes(p.code));
}

/** Machine Group codes reachable through any of the given Plant codes. */
export function groupCodesGrantedByPlants(plantGroups, userPlantCodes) {
  const codes = new Set();
  (plantGroups || [])
    .filter((p) => (userPlantCodes || []).includes(p.code))
    .forEach((p) => (p.groupCodes || []).forEach((c) => codes.add(c)));
  return [...codes];
}

/**
 * filterGroupsForUser, extended with the additive-union access model: a
 * user's effective visible groups = union(user.groups, groups belonging to
 * any plant in user.plantAccess). If a customer never configures Plant
 * Groups, every user's plantAccess is empty, and this collapses to exactly
 * filterGroupsForUser's existing behavior — no behavior change for anyone
 * who hasn't opted into Plant Groups.
 */
export function filterGroupsForUserWithPlants(allGroups, role, userGroupCodes, plantGroups, userPlantCodes) {
  if (role === ROLE_SUPER_ADMIN) return allGroups;
  const effectiveGroupCodes = [
    ...new Set([...(userGroupCodes || []), ...groupCodesGrantedByPlants(plantGroups, userPlantCodes)]),
  ];
  if (effectiveGroupCodes.length === 0) return allGroups; // safe default — no groups, no plants assigned
  return allGroups.filter((g) => effectiveGroupCodes.includes(g.code));
}

/**
 * Restricts a plant-scoped master list (Component/Reason) to what a user can
 * see:
 *  - Super Admin always sees every record.
 *  - A user with no plants assigned sees every record (safe default so
 *    customers that haven't configured Plant Groups, or users predating this
 *    field, aren't locked out).
 *  - Otherwise a record is visible only if its plant is one of the user's —
 *    a record with no plant assigned belongs to no plant, not to every
 *    plant, so a plant-restricted user never sees it.
 */
export function filterByPlantAccess(items, role, userPlantCodes) {
  if (role === ROLE_SUPER_ADMIN) return items;
  if (!userPlantCodes || userPlantCodes.length === 0) return items;
  return items.filter((item) => !!item.plant_code && userPlantCodes.includes(item.plant_code));
}

/**
 * Restricts a list of operators (or any item carrying its own `groups`
 * access list) to those explicitly assigned to one of a given set of
 * Machine Group codes. Deliberately strict — NOT the "unassigned = visible
 * everywhere" default used above: an operator with no `groups` of their own
 * is excluded here too, so a plant with no operators assigned to it shows an
 * empty list rather than silently falling back to every unassigned operator.
 */
export function filterItemsByGroupCodes(items, allowedGroupCodes) {
  return items.filter((item) => !!(item.groups && item.groups.length) && item.groups.some((g) => allowedGroupCodes.includes(g)));
}

/**
 * Machine Group codes belonging to any selected plant. Falls back to every
 * machine-group (unrestricted) when no plants are configured for the
 * customer — the same "unset" fallback the rest of this module uses.
 */
export function resolveAvailableGroupCodes(machineGroups, plantGroups, selectedPlantCodes) {
  if (!plantGroups || plantGroups.length === 0) return machineGroups.map((g) => g.code); // no plants configured
  if (!selectedPlantCodes || selectedPlantCodes.length === 0) return []; // plants exist but none selected

  const codes = new Set();
  plantGroups
    .filter((p) => selectedPlantCodes.includes(p.code))
    .forEach((p) => (p.groupCodes || []).forEach((c) => codes.add(c)));

  return machineGroups.filter((g) => codes.has(g.code)).map((g) => g.code);
}
